import { useEffect, useState } from "react";
import {
  fetchOtoDimensionCandidates,
  type OtoDimensionOption,
} from "../services/objectToObjectPlanService";

export type OtoDimensionCandidatesStatus =
  | "idle"
  | "loading"
  | "ready"
  | "error";

export type UseOtoDimensionCandidatesResult = {
  options: OtoDimensionOption[];
  loading: boolean;
  error: string | null;
  /** True only after a successful fetch for the current A/B type pair. */
  ready: boolean;
  status: OtoDimensionCandidatesStatus;
};

/**
 * Loads dimension candidates for an Object A/B type pair from
 * `GET /assessment-plans/dimensions`.
 */
export function useOtoDimensionCandidates(
  objectAType: string,
  objectBType: string,
): UseOtoDimensionCandidatesResult {
  const [options, setOptions] = useState<OtoDimensionOption[]>([]);
  const [status, setStatus] = useState<OtoDimensionCandidatesStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const a = objectAType.trim();
  const b = objectBType.trim();

  useEffect(() => {
    let mounted = true;

    if (!a || !b) {
      setOptions([]);
      setError(null);
      setStatus("idle");
      return () => {
        mounted = false;
      };
    }

    setStatus("loading");
    setError(null);
    fetchOtoDimensionCandidates(a, b)
      .then((next) => {
        if (!mounted) return;
        setOptions(next);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        // Keep prior options so edit hydrate is not wiped by a failed refetch.
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load dimension candidates",
        );
        setStatus("error");
      });

    return () => {
      mounted = false;
    };
  }, [a, b]);

  return {
    options,
    loading: status === "loading",
    error,
    ready: status === "ready",
    status,
  };
}
